import clsx from 'clsx';
import { InputHTMLAttributes, useEffect, useRef, useState } from 'react';

type SelectOption = {
  label: string;
  value: string | number;
  disabled?: boolean;
};

export type SelectProps = {
  value?: string | number;
  onChange?: (value: string | number) => void;
  options?: (string | number)[] | SelectOption[];
  debounce?: number;
  enableChips?: boolean;
  autoFocus?: boolean;
  size?: 'sm' | 'md';
  invalid?: boolean;
  preventDeselection?: boolean;
  fullWidth?: boolean;
} & Omit<
  InputHTMLAttributes<HTMLSelectElement>,
  'value' | 'onChange' | 'size' | 'id'
>;

export default function Select({
  value: initialValue,
  onChange,
  options,
  debounce,
  enableChips,
  autoFocus,
  size = 'md',
  invalid,
  preventDeselection,
  fullWidth,
  ...props
}: SelectProps) {
  const [value, setValue] = useState(initialValue ?? '');
  const [formattedOptions, setFormattedOptions] = useState<SelectOption[]>([]);
  const isUserInteractionRef = useRef(false);
  const onChangeRef = useRef(onChange);
  const ref = useRef<HTMLSelectElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  onChangeRef.current = onChange;

  useEffect(() => {
    isUserInteractionRef.current = false;
    setValue(initialValue ?? '');
  }, [initialValue]);

  useEffect(() => {
    if (options) {
      if (typeof options[0] === 'object') {
        setFormattedOptions(options as SelectOption[]);
      } else {
        setFormattedOptions(
          (options as (string | number)[]).map((option) => ({
            label: option.toString(),
            value: option
          }))
        );
      }
    }
  }, [JSON.stringify(options)]);

  useEffect(() => {
    if (!isUserInteractionRef.current) {
      return undefined;
    }

    const timeout = setTimeout(() => {
      onChangeRef.current?.(value);
    }, debounce);

    return () => {
      clearTimeout(timeout);
    };
  }, [value, debounce]);

  useEffect(() => {
    if (autoFocus) {
      ref.current?.focus();
      buttonRef.current?.focus();
    }
  }, [autoFocus, formattedOptions]);

  const handleButtonClick = (optionValue: string | number) => {
    isUserInteractionRef.current = true;
    if (value === optionValue && !preventDeselection) {
      setValue('');
    } else {
      setValue(optionValue);
    }
  };

  const selectClasses = clsx(
    'block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500 text-sm',
    props.disabled ? 'opacity-[70%] cursor-not-allowed' : 'cursor-pointer',
    (value === '' || value === undefined || value === null) &&
      !formattedOptions.some((o) => o.value === '') &&
      'text-gray-400',
    size === 'sm' && 'py-1 pl-2 pr-8',
    size === 'md' && 'py-2 pl-3 pr-8',
    invalid && 'ring-2 ring-red-500'
  );

  const buttonClasses = (option: SelectOption, optionIndex: number) => {
    const isSelected = option.value === value;
    const isOptionDisabled = option.disabled && !isSelected;

    return clsx(
      'relative inline-flex items-center py-2 ring-1 ring-inset ring-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 gap-1',
      fullWidth ? 'min-w-0 flex-auto justify-center px-2 text-center' : 'px-3',
      optionIndex > 0 && '-ml-px',
      optionIndex === 0 && 'rounded-l-md',
      optionIndex === (options?.length ?? 0) - 1 && 'rounded-r-md',
      isSelected ? 'bg-blue-200' : 'bg-white hover:bg-blue-100',
      (props.disabled || isOptionDisabled) &&
        'opacity-[70%] cursor-not-allowed text-gray-400',
      !props.disabled && !isOptionDisabled && 'cursor-pointer'
    );
  };

  return enableChips ? (
    <div>
      <span
        className={clsx(
          'isolate rounded-md shadow-sm',
          fullWidth ? 'flex w-full' : 'inline-flex',
          invalid && 'ring-2 ring-red-500'
        )}
      >
        {formattedOptions.map((option, optionIndex) => (
          <button
            type='button'
            key={option.value}
            ref={optionIndex === 0 ? buttonRef : undefined}
            className={buttonClasses(option, optionIndex)}
            value={option.value}
            onClick={() => handleButtonClick(option.value)}
            disabled={props.disabled || option.disabled}
          >
            {option.label}
          </button>
        ))}
      </span>
    </div>
  ) : (
    <select
      {...props}
      ref={ref}
      className={selectClasses}
      value={value}
      onChange={(e) => {
        isUserInteractionRef.current = true;
        setValue(e.target.value);
      }}
    >
      {!formattedOptions.some((o) => o.value === '') && (
        <option
          value=''
          className='text-gray-400'
          disabled={preventDeselection}
        >
          {props.placeholder ?? 'Select...'}
        </option>
      )}
      {formattedOptions.map((option) => (
        <option
          key={option.value}
          value={option.value}
          className='text-black'
          disabled={option.disabled}
        >
          {option.label}
        </option>
      ))}
    </select>
  );
}
